import React, { useState, useCallback, useEffect, useRef } from 'react';
import { HomeView } from './views/HomeView/HomeView';
import { HomeViewModel } from './viewModels/HomeViewModel';
import { ProjectListView } from './views/ProjectListView/ProjectListView';
import { ProjectListViewModel } from './viewModels/ProjectListViewModel';
import { MineView } from './views/MineView/MineView';
import { MineTestView } from './views/MineTestView/MineTestView';
import { UserAgreementView } from './views/UserAgreementView/UserAgreementView';
import { AuthLoginView } from './views/AuthLoginView/AuthLoginView';
import { MyInfoView } from './views/MyInfoView/MyInfoView';
import { LogoutAgreementView } from './views/LogoutAgreementView/LogoutAgreementView';
import { LogoutConfirmModal } from './components/LogoutConfirmModal/LogoutConfirmModal';
import { MyDonationsView } from './views/MyDonationsView/MyDonationsView';
import { MyProjectsView } from './views/MyProjectsView/MyProjectsView';
import { MyCertificatesView } from './views/MyCertificatesView/MyCertificatesView';
import { CertificateView } from './views/CertificateView/CertificateView';
import { DonationDetailView } from './views/DonationDetailView/DonationDetailView';
import { DonationAgreementView } from './views/DonationAgreementView/DonationAgreementView';
import { ProjectDetailView } from './views/ProjectDetailView/ProjectDetailView';
import { MyDonationTeamsView } from './views/MyDonationTeamsView/MyDonationTeamsView';
import { ProjectDetailViewModel } from './viewModels/ProjectDetailViewModel';
import { ProgressDetailViewModel } from './viewModels/ProgressDetailViewModel';
import { ProgressDetailView } from './views/ProgressDetailView/ProgressDetailView';
import { OrganizationDetailViewModel } from './viewModels/OrganizationDetailViewModel';
import { OrganizationDetailView } from './views/OrganizationDetailView/OrganizationDetailView';
import { BottomTabBar, TabType, TEST_TAB_HIDDEN } from './components/BottomTabBar/BottomTabBar';
import { TopBar } from './components/TopBar/TopBar';
import { Project } from './shared/models/Project';
import { Banner } from './shared/models/Banner';
import { Donation } from './shared/models/Donation';
import { DonationTeam } from './shared/models/DonationTeam';
import { DonationConfirmView } from './views/DonationConfirmView/DonationConfirmView';
import { StartDonationTeamView } from './views/StartDonationTeamView/StartDonationTeamView';
import { DonationTeamDetailView } from './views/DonationTeamDetailView/DonationTeamDetailView';
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { logInfo, logError, showError } from './utils/errorHandler';
import { getSavedWechatState, clearWechatState } from './services/WechatAuthService';
import * as WechatAuthService from './services/WechatAuthService';
import { ProjectService } from './services/ProjectService';
import { DonorProfileService } from './services/DonorProfileService';
import { DonationTeamService } from './services/DonationTeamService';
import './App.css';

/**
 * AppContent 组件 - 应用主内容（需要在 OrganizationProvider 内部）
 */
const AppContent: React.FC = () => {
  const { organization, loading, error, isDisabled } = useOrganization();
  const showTestTab = !TEST_TAB_HIDDEN;

  // 监听 organization 状态变化
  React.useEffect(() => {
    console.log('[App] organization state changed (console.log)', {
      hasOrganization: !!organization,
      orgId: organization?.id,
      orgName: organization?.name,
      loading,
      error,
      isDisabled,
      timestamp: new Date().toISOString()
    });
    logInfo('[App] organization state changed', {
      hasOrganization: !!organization,
      orgId: organization?.id,
      orgName: organization?.name,
      loading,
      hasError: !!error,
      isDisabled
    });
  }, [organization, loading, error, isDisabled]);
  
  // 确保 URL 始终是根路径（单页应用，所有路径都应该显示首页）
  React.useEffect(() => {
    if (window.location.pathname !== '/') {
      logInfo('[App] Redirecting to root path', { currentPath: window.location.pathname });
      window.history.replaceState({}, '', '/');
    }
  }, []);
  
  // 处理入口域名回调（URL 中有 openId 参数）
  React.useEffect(() => {
    if (!WechatAuthService.isWechat()) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const openIdFromEntry = urlParams.get('openId');
    
    if (openIdFromEntry) {
      // 从入口域名回调回来的，直接保存 openId 和用户信息
      logInfo('[App] Received openId from entry domain callback', { openId: openIdFromEntry });
      
      WechatAuthService.saveOpenId(openIdFromEntry);
      
      // 如果有昵称和头像，也保存
      const nickname = urlParams.get('nickname');
      const avatar = urlParams.get('avatar');
      if (nickname) {
        WechatAuthService.saveWechatNickname(nickname);
      }
      if (avatar) {
        WechatAuthService.saveWechatAvatar(avatar);
      }
      
      // 清除 URL 中的授权参数
      const url = new URL(window.location.origin + window.location.pathname);
      const otherParams = new URLSearchParams(window.location.search);
      otherParams.delete('openId');
      otherParams.delete('nickname');
      otherParams.delete('avatar');
      otherParams.delete('unionId');
      if (otherParams.toString()) {
        url.search = otherParams.toString();
      }
      window.history.replaceState({}, '', url.toString());
      
      logInfo('[App] Processed entry domain callback, saved user info');
    }
  }, []);
  
  // 使用函数初始化器恢复保存的tab状态，避免授权回调后先显示HOME
  const [activeTab, setActiveTabState] = useState<TabType>(() => {
    const savedTab = localStorage.getItem('pending_active_tab');
    if (savedTab && (savedTab === 'home' || savedTab === 'project' || savedTab === 'mine' || (!TEST_TAB_HIDDEN && savedTab === 'mine_test'))) {
      logInfo('[App] Restoring active tab from localStorage', { savedTab });
      return savedTab as TabType;
    }
    return TabType.HOME;
  });
  
  // 包装setActiveTab，切换tab时重置滚动位置
  const setActiveTab = useCallback((tab: TabType) => {
    setActiveTabState(tab);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [progressDetailId, setProgressDetailId] = useState<string | null>(null);
  const [progressDetailProjectId, setProgressDetailProjectId] = useState<string | null>(null);
  const [organizationDetailId, setOrganizationDetailId] = useState<string | null>(null);
  const [showUserAgreement, setShowUserAgreement] = useState(false);
  const [showDonationAgreement, setShowDonationAgreement] = useState(false);
  const [showMyInfo, setShowMyInfo] = useState(false);
  const [showMyDonations, setShowMyDonations] = useState(false);
  const [showMyProjects, setShowMyProjects] = useState(false);
  const [showMyDonationTeams, setShowMyDonationTeams] = useState(false);
  const [myDonationTeamsTab, setMyDonationTeamsTab] = useState<'initiated' | 'joined'>('initiated');
  const [showMyCertificates, setShowMyCertificates] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [selectedCertificateId, setSelectedCertificateId] = useState<string | null>(null);
  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null);
  const [selectedProjectIdForDonations, setSelectedProjectIdForDonations] = useState<string | null>(null);
  const [showLogoutAgreement, setShowLogoutAgreement] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // 使用函数初始化器恢复支付授权状态，避免授权回调后先显示HOME
  const [showAuthLoginForPayment, setShowAuthLoginForPayment] = useState<{
    project: Project;
    amount: number;
    recurring: boolean;
    anonymous: boolean;
    teamId?: string;
  } | null>(() => {
    const savedPaymentInfo = localStorage.getItem('pending_donation_info');
    if (savedPaymentInfo) {
      try {
        const paymentInfo = JSON.parse(savedPaymentInfo);
        logInfo('[App] Restoring showAuthLoginForPayment from localStorage', {
          projectId: paymentInfo.projectId
        });
        // 返回临时对象，后续在useEffect中加载完整的Project对象
        return {
          project: { id: paymentInfo.projectId } as Project,
          amount: paymentInfo.amount,
          recurring: paymentInfo.recurring,
          anonymous: paymentInfo.anonymous,
          teamId: paymentInfo.teamId,
        };
      } catch (e) {
        logError('[App] Failed to parse saved payment info', e);
        return null;
      }
    }
    return null;
  });
  const [showAuthLoginForTeam, setShowAuthLoginForTeam] = useState<{ projectId: string } | null>(() => {
    const savedProjectId = localStorage.getItem('pending_start_team_project_id');
    if (!savedProjectId) {
      return null;
    }
    return { projectId: savedProjectId };
  });
  const [startTeamProject, setStartTeamProject] = useState<Project | null>(null);
  const [teamDetailEntry, setTeamDetailEntry] = useState<{ team: DonationTeam; project: Project } | null>(null);
  const hasHandledTeamShareRef = useRef(false);
  const [donationConfirm, setDonationConfirm] = useState<{
    project: Project;
    amount: number;
    recurring: boolean;
    anonymous: boolean;
    teamId?: string;
  } | null>(null);

  // 监听所有页面状态变化，切换页面时重置滚动位置
  useEffect(() => {
    // 当页面状态发生变化时，重置滚动位置
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [
    activeTab,
    detailId,
    progressDetailId,
    organizationDetailId,
    showUserAgreement,
    showDonationAgreement,
    showMyInfo,
    showMyDonations,
    showMyProjects,
    showMyDonationTeams,
    showMyCertificates,
    showCertificate,
    selectedDonation,
    selectedProjectIdForDonations,
    showLogoutAgreement,
    showAuthLoginForPayment,
    showAuthLoginForTeam,
    startTeamProject,
    teamDetailEntry,
    donationConfirm,
  ]);

  // 监听 donationConfirm 状态变化
  React.useEffect(() => {
    console.log('[App] donationConfirm state changed (console.log)', {
      hasDonationConfirm: !!donationConfirm,
      projectId: donationConfirm?.project?.id,
      amount: donationConfirm?.amount,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n')
    });
    logInfo('[App] donationConfirm state changed', {
      hasDonationConfirm: !!donationConfirm,
      projectId: donationConfirm?.project?.id,
      amount: donationConfirm?.amount,
      recurring: donationConfirm?.recurring,
      anonymous: donationConfirm?.anonymous
    });
  }, [donationConfirm]);

  // 恢复支付流程（授权回调后）
  React.useEffect(() => {
    const restorePaymentFlow = async () => {
      // 检查是否有保存的 state（项目ID）
      const savedState = getSavedWechatState();
      if (!savedState) {
        return; // 没有保存的 state，不需要恢复
      }

      // 检查是否已经获取到 openId（授权完成）
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        return; // 还没有 openId，等待授权完成
      }

      // 检查是否已经有 donationConfirm（避免重复恢复）
      if (donationConfirm) {
        logInfo('[App] donationConfirm already exists, clearing saved state', {
          savedState,
          existingProjectId: donationConfirm.project.id
        });
        clearWechatState();
        return;
      }

      // 检查机构是否已加载
      if (!organization || loading) {
        return; // 等待机构加载完成
      }

      try {
        logInfo('[App] Restoring payment flow from auth callback', {
          savedState,
          hasOpenId: !!openId,
          orgId: organization.id
        });

        // 从 localStorage 获取保存的支付信息
        const savedPaymentInfo = localStorage.getItem('pending_donation_info');
        if (!savedPaymentInfo) {
          logError('[App] No saved payment info found, cannot restore payment flow');
          clearWechatState();
          return;
        }

        const paymentInfo = JSON.parse(savedPaymentInfo);
        const { projectId, amount, recurring, anonymous, teamId } = paymentInfo;

        // 验证 state 是否匹配
        if (savedState !== projectId) {
          logError('[App] State mismatch, cannot restore payment flow', {
            savedState,
            projectId
          });
          clearWechatState();
          localStorage.removeItem('pending_donation_info');
          return;
        }

        // 获取项目信息
        logInfo('[App] Fetching project info for payment restoration', { projectId });
        const project = await ProjectService.getProjectById(projectId);

        // 清除授权登录页面状态
        setShowAuthLoginForPayment(null);

        // 恢复 donationConfirm 状态
        logInfo('[App] Restoring donationConfirm state', {
          projectId: project.id,
          amount,
          recurring,
          anonymous
        });
        setDonationConfirm({
          project,
          amount,
          recurring,
          anonymous,
          teamId,
        });

        // 清除保存的状态（但保留用户输入的姓名和电话，由组件自己恢复）
        clearWechatState();
        localStorage.removeItem('pending_donation_info');
        localStorage.removeItem('pending_active_tab'); // 清除保存的tab状态
        // 注意：不删除 pending_donor_info，让组件自己恢复

        logInfo('[App] Payment flow restored successfully');
      } catch (error) {
        logError('[App] Failed to restore payment flow', error);
        // 显示错误弹窗
        const errorMessage = error instanceof Error ? error.message : `恢复支付流程失败: ${String(error)}`;
        showError(errorMessage, 'App.restorePaymentFlow');
        clearWechatState();
        localStorage.removeItem('pending_donation_info');
        localStorage.removeItem('pending_donor_info');
        localStorage.removeItem('pending_active_tab'); // 清除保存的tab状态
        setShowAuthLoginForPayment(null);
      }
    };

    restorePaymentFlow();
  }, [organization, loading, donationConfirm, activeTab]);

  // 恢复一起捐发起流程（授权回调后）
  React.useEffect(() => {
    const restoreStartTeamFlow = async () => {
      const pendingProjectId = localStorage.getItem('pending_start_team_project_id');
      if (!pendingProjectId) {
        return;
      }
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        return;
      }
      if (!organization || loading) {
        return;
      }
      if (startTeamProject && startTeamProject.id === pendingProjectId) {
        localStorage.removeItem('pending_start_team_project_id');
        localStorage.removeItem('pending_active_tab');
        setShowAuthLoginForTeam(null);
        return;
      }

      try {
        const project = await ProjectService.getProjectById(pendingProjectId);
        setShowAuthLoginForTeam(null);
        setStartTeamProject(project);
        localStorage.removeItem('pending_start_team_project_id');
        localStorage.removeItem('pending_active_tab');
      } catch (error) {
        logError('[App] Failed to restore start team flow', error);
        showError('授权成功，但恢复发起一起捐页面失败，请重试', 'App.restoreStartTeamFlow');
        localStorage.removeItem('pending_start_team_project_id');
        localStorage.removeItem('pending_active_tab');
        setShowAuthLoginForTeam(null);
      }
    };

    restoreStartTeamFlow();
  }, [organization, loading, startTeamProject]);

  useEffect(() => {
    if (loading || !organization || hasHandledTeamShareRef.current) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const shareTeamId = params.get('teamId');
    if (!shareTeamId) {
      hasHandledTeamShareRef.current = true;
      return;
    }

    hasHandledTeamShareRef.current = true;
    const loadSharedTeam = async () => {
      try {
        const openId = WechatAuthService.getOpenId() || undefined;
        const team = await DonationTeamService.getTeamById(shareTeamId, openId);
        const project = await ProjectService.getProjectById(team.projectId);
        setDetailId(null);
        setShowMyDonationTeams(false);
        setTeamDetailEntry({ team, project });
      } catch (error) {
        logError('[App] Failed to open shared team detail', error);
        showError('加载一起捐详情失败', 'App.openSharedTeamDetail');
      }
    };
    loadSharedTeam();
  }, [loading, organization]);
  
  // 为每个页面创建独立的 ViewModel 实例
  const homeViewModel = React.useMemo(() => new HomeViewModel(), []);
  const projectListViewModel = React.useMemo(() => new ProjectListViewModel(), []);
  const projectDetailViewModel = React.useMemo(() => new ProjectDetailViewModel(), []);
  const progressDetailViewModel = React.useMemo(() => new ProgressDetailViewModel(), []);
  const organizationDetailViewModel = React.useMemo(() => new OrganizationDetailViewModel(), []);

  // 加载中
  if (loading) {
    return (
      <div className="app-container">
        <div className="app-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div>加载机构信息中...</div>
        </div>
      </div>
    );
  }

  // 加载失败
  if (error || !organization) {
    return (
      <div className="app-container">
        <div className="app-content" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: '20px' }}>
          <div style={{ fontSize: '16px', color: '#f5222d', marginBottom: '10px' }}>机构信息加载失败</div>
          <div style={{ fontSize: '14px', color: '#666' }}>{error || '未找到对应机构'}</div>
          <button 
            onClick={() => window.location.reload()} 
            style={{ marginTop: '20px', padding: '8px 16px', borderRadius: '4px', border: '1px solid #d9d9d9', background: '#fff', cursor: 'pointer' }}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  // 机构被禁用（维护中）
  if (isDisabled) {
    return (
      <div className="app-container">
        <div className="app-content" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh', 
          padding: '40px 20px',
          background: 'var(--primary-gradient)',
          color: '#fff'
        }}>
          <div style={{ 
            fontSize: '48px', 
            marginBottom: '20px',
            opacity: 0.9
          }}>🔧</div>
          <div style={{ 
            fontSize: '24px', 
            fontWeight: 'bold', 
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            网站正在维护中
          </div>
          <div style={{ 
            fontSize: '16px', 
            marginBottom: '8px',
            textAlign: 'center',
            opacity: 0.9
          }}>
            {organization.name || '本机构'}暂时无法访问
          </div>
          <div style={{ 
            fontSize: '14px', 
            opacity: 0.8,
            textAlign: 'center'
          }}>
            我们正在进行系统维护，请稍后再试
          </div>
          <div style={{ 
            marginTop: '30px',
            fontSize: '13px',
            opacity: 0.7
          }}>
            如有疑问，请联系机构管理员
          </div>
        </div>
      </div>
    );
  }

  const handleViewAllProjects = (payload: {
    projects: Project[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    hasMore: boolean;
    banners: Banner[];
  }) => {
    projectListViewModel.hydrateFromHome({
      projects: payload.projects,
      pagination: payload.pagination,
      hasMore: payload.hasMore,
      banners: payload.banners,
    });
    setActiveTab(TabType.PROJECT);
  };

  const openDetail = (project: Project) => {
    setDetailId(project.id);
    projectDetailViewModel.load(project.id);
  };

  const openTeamDetail = async (team: DonationTeam) => {
    const project = await ProjectService.getProjectById(team.projectId);
    setShowMyDonationTeams(false);
    setDetailId(null);
    setTeamDetailEntry({ team, project });
  };

  const handleApplyLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息，无法注销', 'App.handleConfirmLogout');
        return;
      }
      await DonorProfileService.deactivateAccount(openId);
      logInfo('[App] Account deactivated successfully');

      // 清除所有授权缓存
      WechatAuthService.clearOpenId();
      WechatAuthService.clearWechatNickname();
      WechatAuthService.clearWechatAvatar();
      WechatAuthService.clearWechatState();
      localStorage.removeItem('pending_donation_info');
      localStorage.removeItem('pending_donor_info');

      // 返回首页
      setShowLogoutAgreement(false);
      setShowMyInfo(false);
      setActiveTab(TabType.HOME);
      alert('账号已成功注销');
    } catch (error) {
      logError('[App] Failed to deactivate account', error);
      showError('注销账号失败', 'App.handleConfirmLogout');
    }
  };

  const closeDetail = () => {
    setDetailId(null);
  };

  const openProgressDetail = (progressId: string, projectId: string) => {
    setProgressDetailId(progressId);
    setProgressDetailProjectId(projectId);
    progressDetailViewModel.load(progressId, projectId);
  };

  const closeProgressDetail = () => {
    setProgressDetailId(null);
    setProgressDetailProjectId(null);
  };

  const openOrganizationDetail = (organizationId: string, organization?: any) => {
    setOrganizationDetailId(organizationId);
    organizationDetailViewModel.load(organizationId, organization);
  };

  const closeOrganizationDetail = () => {
    setOrganizationDetailId(null);
    organizationDetailViewModel.reset();
  };

  const renderContent = () => {
    console.log('[App] renderContent called (console.log)', {
      hasDonationConfirm: !!donationConfirm,
      hasDetailId: !!detailId,
      hasProgressDetailId: !!progressDetailId,
      hasOrganizationDetailId: !!organizationDetailId,
      showUserAgreement,
      showAuthLoginForPayment: !!showAuthLoginForPayment,
      activeTab,
      timestamp: new Date().toISOString()
    });
    
    if (showUserAgreement) {
      return (
        <UserAgreementView
          onBack={() => setShowUserAgreement(false)}
        />
      );
    }
    
    if (showDonationAgreement) {
      // 如果同时有 detailId，需要保留 ProjectDetailView 的渲染以保持 modal 状态
      if (detailId) {
        return (
          <>
            {/* 渲染 ProjectDetailView，但隐藏主要内容和 modal */}
            <ProjectDetailView
              viewModel={projectDetailViewModel}
              onBack={closeDetail}
              onGoHome={() => {
                closeDetail();
                setActiveTab(TabType.HOME);
              }}
              onProgressClick={(p) => openProgressDetail(p.id, p.projectId)}
              onOrganizationClick={openOrganizationDetail}
              onAgreementClick={() => setShowDonationAgreement(true)}
              onDonate={async ({ amount, recurring, anonymous, teamId }, project) => {
                // 占位，不会被执行
              }}
              hideContent={true}
              hideModal={true}
            />
            {/* 显示捐赠协议页面，使用 position: fixed 覆盖在上面，z-index 低于 modal (999)；高度限定为视口以便内容区可滚动 */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 100,
                backgroundColor: '#fff',
              }}
            >
              <DonationAgreementView
                onBack={() => setShowDonationAgreement(false)}
              />
            </div>
          </>
        );
      } else {
        return (
          <DonationAgreementView
            onBack={() => setShowDonationAgreement(false)}
          />
        );
      }
    }
    
    if (showLogoutAgreement) {
      return (
        <>
          <LogoutAgreementView
            onBack={() => setShowLogoutAgreement(false)}
            onApplyLogout={handleApplyLogout}
          />
          <LogoutConfirmModal
            visible={showLogoutConfirm}
            onCancel={() => setShowLogoutConfirm(false)}
            onConfirm={handleConfirmLogout}
          />
        </>
      );
    }
    
    if (showMyInfo) {
      return (
        <>
          <MyInfoView
            onBack={() => setShowMyInfo(false)}
            onLogoutClick={() => {
              logInfo('[App] onLogoutClick called, setting showLogoutAgreement=true and showMyInfo=false');
              // 使用 setTimeout 确保状态更新顺序，先设置 showLogoutAgreement
              setShowLogoutAgreement(true);
              // 延迟设置 showMyInfo，确保 showLogoutAgreement 先生效
              setTimeout(() => {
                setShowMyInfo(false);
              }, 0);
            }}
          />
        </>
      );
    }
    
    if (selectedDonation) {
      return (
        <DonationDetailView
          donation={selectedDonation}
          onBack={() => {
            setSelectedDonation(null);
            setShowMyDonations(true);
          }}
          onProjectClick={(projectId) => {
            setSelectedDonation(null);
            setShowMyDonations(false);
            setDetailId(projectId);
            projectDetailViewModel.load(projectId);
          }}
        />
      );
    }
    
    if (selectedProjectIdForDonations) {
      // 显示特定项目的捐赠记录
      return (
        <MyDonationsView
          onBack={() => setSelectedProjectIdForDonations(null)}
          projectId={selectedProjectIdForDonations}
          onDonationClick={(donation) => {
            setSelectedDonation(donation);
            setSelectedProjectIdForDonations(null);
          }}
        />
      );
    }

    if (showMyDonations) {
      return (
        <MyDonationsView
          onBack={() => setShowMyDonations(false)}
          onDonationClick={(donation) => {
            setSelectedDonation(donation);
          }}
        />
      );
    }

    if (showMyProjects) {
      return (
        <MyProjectsView
          onBack={() => setShowMyProjects(false)}
          onProjectClick={(projectId) => {
            setShowMyProjects(false);
            setDetailId(projectId);
            projectDetailViewModel.load(projectId);
          }}
          onDonationRecordClick={(projectId) => {
            setSelectedProjectIdForDonations(projectId);
            setShowMyProjects(false);
          }}
        />
      );
    }

    if (showMyDonationTeams) {
      return (
        <MyDonationTeamsView
          initialTab={myDonationTeamsTab}
          onBack={() => setShowMyDonationTeams(false)}
          onTeamClick={(team) => {
            openTeamDetail(team).catch((error) => {
              logError('[App] Failed to open team detail from my teams', error);
              showError('打开团队详情失败', 'App.openTeamDetail');
            });
          }}
        />
      );
    }

    if (showCertificate && selectedCertificateId) {
      return (
        <CertificateView
          donationId={selectedCertificateId}
          onBack={() => {
            setShowCertificate(false);
            setSelectedCertificateId(null);
            setShowMyCertificates(true);
          }}
        />
      );
    }

    if (showMyCertificates) {
      return (
        <MyCertificatesView
          onBack={() => setShowMyCertificates(false)}
          onViewCertificate={(donation, project) => {
            setSelectedCertificateId(donation.id);
            setShowCertificate(true);
            setShowMyCertificates(false);
            logInfo('[App] View certificate clicked', {
              donationId: donation.id,
              projectId: project.id,
            });
          }}
        />
      );
    }
    
    if (showAuthLoginForPayment) {
      return (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          <TopBar 
            title="注册登录" 
            showBack 
            onBack={() => {
              setShowAuthLoginForPayment(null);
              localStorage.removeItem('pending_donation_info');
              localStorage.removeItem('pending_active_tab'); // 清除保存的tab状态
              clearWechatState();
            }}
          />
          <AuthLoginView
            onAgreementClick={() => setShowUserAgreement(true)}
            context="payment"
            paymentInfo={{
              projectId: showAuthLoginForPayment.project.id,
              amount: showAuthLoginForPayment.amount,
              recurring: showAuthLoginForPayment.recurring,
              anonymous: showAuthLoginForPayment.anonymous,
              teamId: showAuthLoginForPayment.teamId,
            }}
          />
        </div>
      );
    }

    if (showAuthLoginForTeam) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <TopBar
            title="注册登录"
            showBack
            onBack={() => {
              setShowAuthLoginForTeam(null);
              localStorage.removeItem('pending_start_team_project_id');
              localStorage.removeItem('pending_active_tab');
              clearWechatState();
            }}
          />
          <AuthLoginView
            onAgreementClick={() => setShowUserAgreement(true)}
            context="team"
            teamInfo={{ projectId: showAuthLoginForTeam.projectId }}
          />
        </div>
      );
    }

    if (startTeamProject) {
      return (
        <StartDonationTeamView
          project={startTeamProject}
          onBack={() => setStartTeamProject(null)}
          onProjectClick={() => setStartTeamProject(null)}
          teamAvatar={WechatAuthService.getWechatAvatar() || undefined}
          onSubmit={async ({ name, slogan, targetAmount }) => {
            const openId = WechatAuthService.getOpenId();
            if (!openId) {
              showError('请先完成微信授权后再发起一起捐', 'App.StartDonationTeamSubmit');
              return;
            }
            const team = await DonationTeamService.createTeam({
              projectId: startTeamProject.id,
              creatorOpenId: openId,
              name,
              slogan,
              avatar: WechatAuthService.getWechatAvatar() || undefined,
              targetAmount,
            });
            await projectDetailViewModel.load(startTeamProject.id);
            setStartTeamProject(null);
            setTeamDetailEntry({ team, project: startTeamProject });
          }}
        />
      );
    }

    if (teamDetailEntry) {
      return (
        <DonationTeamDetailView
          team={teamDetailEntry.team}
          project={teamDetailEntry.project}
          onBack={() => setTeamDetailEntry(null)}
          onGoHome={() => {
            setTeamDetailEntry(null);
            setActiveTab(TabType.HOME);
          }}
          onAgreementClick={() => setShowDonationAgreement(true)}
          onDonate={async ({ amount, recurring, anonymous, teamId }, project) => {
            if (!project) return;
            if (WechatAuthService.isWechat()) {
              const openId = WechatAuthService.getOpenId();
              if (!openId) {
                const paymentInfo = { projectId: project.id, amount, recurring, anonymous, teamId };
                localStorage.setItem('pending_donation_info', JSON.stringify(paymentInfo));
                localStorage.setItem('pending_active_tab', activeTab);
                setShowAuthLoginForPayment({ project, amount, recurring, anonymous, teamId });
                return;
              }
            }
            setDonationConfirm({ project, amount, recurring, anonymous, teamId });
          }}
        />
      );
    }
    
    if (donationConfirm) {
      console.log('[App] Rendering DonationConfirmView (console.log)', {
        projectId: donationConfirm.project.id,
        amount: donationConfirm.amount
      });
      return (
        <DonationConfirmView
          project={donationConfirm.project}
          amount={donationConfirm.amount}
          recurring={donationConfirm.recurring}
          anonymous={donationConfirm.anonymous}
          teamId={donationConfirm.teamId}
          onBack={() => {
            console.log('[App] DonationConfirmView onBack called (console.log)');
            logInfo('[App] DonationConfirmView onBack called');
            // 清除保存的支付信息和用户输入
            clearWechatState();
            localStorage.removeItem('pending_donation_info');
            localStorage.removeItem('pending_donor_info');
            setDonationConfirm(null);
          }}
          onFinish={() => {
            console.log('[App] DonationConfirmView onFinish called (console.log)');
            logInfo('[App] DonationConfirmView onFinish called');
            // 清除保存的支付信息和用户输入
            clearWechatState();
            localStorage.removeItem('pending_donation_info');
            localStorage.removeItem('pending_donor_info');
            setDonationConfirm(null);
          }}
          onViewCertificate={(donationId) => {
            clearWechatState();
            localStorage.removeItem('pending_donation_info');
            localStorage.removeItem('pending_donor_info');
            setDonationConfirm(null);
            setSelectedCertificateId(donationId);
            setShowCertificate(true);
          }}
        />
      );
    }
    if (organizationDetailId) {
      return (
        <OrganizationDetailView
          viewModel={organizationDetailViewModel}
          onBack={closeOrganizationDetail}
        />
      );
    }
    if (progressDetailId) {
      return (
        <ProgressDetailView
          viewModel={progressDetailViewModel}
          onBack={closeProgressDetail}
          onProjectClick={(pid) => {
            closeProgressDetail();
            setDetailId(pid);
            projectDetailViewModel.load(pid);
          }}
        />
      );
    }
    if (detailId) {
      return (
        <ProjectDetailView
          viewModel={projectDetailViewModel}
          onBack={closeDetail}
          onGoHome={() => {
            closeDetail();
            setActiveTab(TabType.HOME);
          }}
          onProgressClick={(p) => openProgressDetail(p.id, p.projectId)}
          onOrganizationClick={openOrganizationDetail}
          onAgreementClick={() => setShowDonationAgreement(true)}
          onStartTeam={async (project) => {
            const openId = WechatAuthService.getOpenId();
            if (!openId) {
              localStorage.setItem('pending_start_team_project_id', project.id);
              localStorage.setItem('pending_active_tab', activeTab);
              setShowAuthLoginForTeam({ projectId: project.id });
              return;
            }
            setStartTeamProject(project);
          }}
          onOpenMyTeams={(project, tab) => {
            setMyDonationTeamsTab(tab);
            setShowMyDonationTeams(true);
          }}
          onJoinTeam={async (team, project) => {
            const openId = WechatAuthService.getOpenId();
            if (!openId) {
              showError('请先完成微信授权后再参与一起捐', 'App.onJoinTeam');
              return false;
            }
            try {
              await DonationTeamService.joinTeam(team.id, openId);
              await projectDetailViewModel.load(project.id);
              return true;
            } catch (e) {
              const msg = e instanceof Error ? e.message : '加入失败';
              showError(msg, 'App.onJoinTeam');
              return false;
            }
          }}
          onTeamClick={(team) => {
            openTeamDetail(team).catch((error) => {
              logError('[App] Failed to open team detail from project list', error);
              showError('打开团队详情失败', 'App.openTeamDetailFromProject');
            });
          }}
          onDonate={async ({ amount, recurring, anonymous, teamId }, project) => {
            logInfo('[App] onDonate callback called', {
              amount,
              recurring,
              anonymous,
              hasProject: !!project,
              projectId: project?.id,
              projectTitle: project?.title
            });
            
            if (!project) {
              logError('[App] onDonate called but project is null');
              return;
            }

            // 检查授权状态
            if (WechatAuthService.isWechat()) {
              // 检查是否已有 openId
              const openId = WechatAuthService.getOpenId();
              
              // 检查用户状态（如果已有openId）
              if (openId) {
                try {
                  const userProfile = await DonorProfileService.getProfile(openId);
                  if (userProfile && userProfile.status === 'DEACTIVATED') {
                    showError('您的账号已注销，无法进行支付', 'App.onDonate');
                  return;
                  }
                } catch (error) {
                  logError('[App] Failed to check user status', error);
                  // 继续支付流程，不阻止
                }
              }

              // 如果没有 openId，显示授权登录页面
              if (!openId) {
                logInfo('[App] No openId, showing auth login page for payment');

                // 保存支付信息到 localStorage（用于授权回调后恢复）
                const paymentInfo = {
                  projectId: project.id,
                  amount,
                  recurring,
                  anonymous,
                  teamId,
                };
                localStorage.setItem('pending_donation_info', JSON.stringify(paymentInfo));
                logInfo('[App] Saved payment info for restoration after auth', paymentInfo);

                // 显示授权登录页面
                // 保存当前tab状态，用于授权回调后恢复
                localStorage.setItem('pending_active_tab', activeTab);
                logInfo('[App] Saved active tab before payment auth', { activeTab });
                
                setShowAuthLoginForPayment({
                  project,
                  amount,
                  recurring,
                  anonymous,
                  teamId,
                });
                return; // 显示授权页面，不继续执行
              }
            }

            // 有 openId 了，直接进入支付确认页面
            console.log('[App] Setting donationConfirm state (console.log)', {
              projectId: project.id,
              amount,
              recurring,
              anonymous,
              timestamp: new Date().toISOString()
            });
            logInfo('[App] Setting donationConfirm state', {
              projectId: project.id,
              amount,
              recurring,
              anonymous
            });
              setDonationConfirm({
                project,
                amount,
                recurring,
                anonymous,
                teamId,
              });
            console.log('[App] donationConfirm state set (console.log)');
            logInfo('[App] donationConfirm state set');
          }}
        />
      );
    }

    switch (activeTab) {
      case TabType.HOME:
        return (
          <HomeView
            viewModel={homeViewModel}
            onViewAllProjects={handleViewAllProjects}
            onProjectClick={openDetail}
            onProgressClick={(p) => openProgressDetail(p.id, p.projectId)}
            onOrganizationClick={openOrganizationDetail}
          />
        );
      case TabType.PROJECT:
        return <ProjectListView viewModel={projectListViewModel} onProjectClick={openDetail} />;
      case TabType.MINE:
        return (
            <MineView
              onAgreementClick={() => setShowUserAgreement(true)}
              onMyInfoClick={() => setShowMyInfo(true)}
              onMyDonationsClick={() => setShowMyDonations(true)}
              onMyProjectsClick={() => setShowMyProjects(true)}
              onMyCertificatesClick={() => setShowMyCertificates(true)}
              onMyTeamsClick={() => {
                setMyDonationTeamsTab('initiated');
                setShowMyDonationTeams(true);
              }}
            />
        );
      case TabType.MINE_TEST:
        return showTestTab ? <MineTestView /> : (
          <HomeView
            viewModel={homeViewModel}
            onViewAllProjects={handleViewAllProjects}
            onProjectClick={openDetail}
            onOrganizationClick={openOrganizationDetail}
          />
        );
      default:
        return (
          <HomeView
            viewModel={homeViewModel}
            onViewAllProjects={handleViewAllProjects}
            onProjectClick={openDetail}
            onOrganizationClick={openOrganizationDetail}
          />
        );
    }
  };

  return (
    <div className="app-container">
      <div className="app-content">
        {renderContent()}
      </div>
      {!detailId && !progressDetailId && !donationConfirm && !organizationDetailId && !showUserAgreement && !showDonationAgreement && !showMyInfo && !showMyDonations && !showMyProjects && !showMyDonationTeams && !showMyCertificates && !showCertificate && !selectedDonation && !selectedProjectIdForDonations && !showLogoutAgreement && !showAuthLoginForPayment && !showAuthLoginForTeam && !startTeamProject && !teamDetailEntry && <BottomTabBar activeTab={activeTab} onTabChange={setActiveTab} />}
    </div>
  );
};

/**
 * App 组件 - 应用入口
 * 调试：访问 ?minimal=1 可只渲染简单内容，用于排查白屏是否来自机构/业务逻辑
 */
const App: React.FC = () => {
  if (typeof window !== 'undefined' && window.location.search.includes('minimal=1')) {
    return (
      <div style={{ padding: 20, fontSize: 18, minHeight: '100vh', background: '#f5f5f5' }}>
        H5 最小渲染正常。去掉 URL 中的 ?minimal=1 可恢复完整页面。
      </div>
    );
  }
  return (
    <ErrorBoundary>
      <ErrorProvider>
        <OrganizationProvider>
          <AppContent />
        </OrganizationProvider>
      </ErrorProvider>
    </ErrorBoundary>
  );
};

export default App;
